import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  ActivityEventView,
  AddCommentInput,
  AddLineInput,
  CancelWholesaleOrderInput,
  CommentView,
  CreateWholesaleCustomerInput,
  CreateWholesaleOrderInput,
  CursorPage,
  MarkReadInput,
  MutationMeta,
  PreviewReceiptInput,
  PreviewReceiptResult,
  ReverseVerificationInput,
  SetLineExceptionInput,
  UpdateLineInput,
  UpdateWholesaleCustomerInput,
  VerifyReceiptInput,
  WholesaleCustomerView,
  WholesaleDuplicateCandidate,
  WholesaleEmailPreview,
  WholesaleEmailView,
  WholesaleMutationResult,
  WholesaleOrderDetail,
  WholesaleOrderHeaderPatch,
  WholesaleOrderSummary,
  WholesaleSearchHit,
} from "../../shared/contracts.js";
import type { WholesaleListView } from "../../shared/domain.js";
import { invalidateCaseData } from "./caseMutations.js";
import { LIST_PAGE_SIZE, type InfinitePages } from "./caseQueries.js";
import { isClientApiError, request } from "./http.js";

/**
 * The wholesale side's API (spec 2026-09-17 §4, §5): customers, orders,
 * lists, search, the detail, and every Parts action on a wholesale order,
 * as React Query hooks under the `["wholesale", …]` root.
 *
 * As on the service side the client asks and displays, never derives:
 * which orders are Open, whether an Arrived one is still inside its 36
 * hours, the status, the capabilities and the unread flag all come from
 * the server and are rendered as returned. Every response is per-user and
 * `no-store`.
 *
 * The line, receipt, comment and read bodies are the case routes' own
 * (`AddLineInput`, `VerifyReceiptInput`, …), so the shared dialogs send
 * one shape on both sides and differ only in the path they post to — the
 * `endpoints` objects at the bottom are what the dialogs take. After any
 * success the whole data cache is invalidated ({@link invalidateCaseData}
 * marks everything but the session stale, on either side), so every open
 * panel shows the server's new truth.
 */

// ---------------------------------------------------------------------------
// DTOs as they cross the wire
// ---------------------------------------------------------------------------

/** `WholesaleMutationResult` as JSON: `arrivedAt` travels as an ISO string. */
export type WholesaleMutationResultDto = Readonly<
  Omit<WholesaleMutationResult, "arrivedAt"> & { arrivedAt: string | null }
>;

// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------

export type WholesaleListParams = Readonly<{
  view: WholesaleListView;
  customerId?: string | null;
}>;

export type CustomerListParams = Readonly<{
  q?: string;
  includeInactive?: boolean;
}>;

export const wholesaleQueryKeys = {
  all: () => ["wholesale"] as const,
  lists: () => ["wholesale", "list"] as const,
  list: (params: WholesaleListParams) =>
    ["wholesale", "list", { view: params.view, customerId: params.customerId ?? null }] as const,
  searches: () => ["wholesale", "search"] as const,
  search: (query: string) => ["wholesale", "search", query.trim()] as const,
  details: () => ["wholesale", "detail"] as const,
  detail: (orderId: string) => ["wholesale", "detail", orderId] as const,
  olderActivity: (orderId: string, fromCursor: string | null) =>
    ["wholesale", "detail", orderId, "older-activity", { from: fromCursor }] as const,
  emailPreview: (orderId: string) => ["wholesale", "detail", orderId, "email-preview"] as const,
  customers: () => ["wholesale", "customers"] as const,
  customerList: (params: CustomerListParams) =>
    ["wholesale", "customers", "list", { q: (params.q ?? "").trim(), includeInactive: params.includeInactive ?? false }] as const,
  customer: (customerId: string) => ["wholesale", "customers", "one", customerId] as const,
} as const;

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

const ORDERS = "/api/wholesale/orders";
const CUSTOMERS = "/api/wholesale/customers";

function order(orderId: string): string {
  return `${ORDERS}/${encodeURIComponent(orderId)}`;
}

function customer(customerId: string): string {
  return `${CUSTOMERS}/${encodeURIComponent(customerId)}`;
}

export const wholesalePaths = {
  orders: ORDERS,
  list: (params: WholesaleListParams, cursor?: string | null, limit: number = LIST_PAGE_SIZE) => {
    const search = new URLSearchParams();
    search.set("view", params.view);
    if (params.customerId) search.set("customerId", params.customerId);
    if (cursor) search.set("cursor", cursor);
    search.set("limit", String(limit));
    return `${ORDERS}?${search.toString()}`;
  },
  search: (query: string, cursor?: string | null, limit: number = LIST_PAGE_SIZE) => {
    const search = new URLSearchParams();
    search.set("q", query.trim());
    if (cursor) search.set("cursor", cursor);
    search.set("limit", String(limit));
    return `${ORDERS}/search?${search.toString()}`;
  },
  detail: order,
  activityPage: (orderId: string, cursor: string) => `${order(orderId)}?activityCursor=${encodeURIComponent(cursor)}`,
  cancel: (orderId: string) => `${order(orderId)}/cancel`,
  addLine: (orderId: string) => `${order(orderId)}/lines`,
  line: (orderId: string, lineId: string) => `${order(orderId)}/lines/${encodeURIComponent(lineId)}`,
  lineException: (orderId: string, lineId: string) =>
    `${order(orderId)}/lines/${encodeURIComponent(lineId)}/exception`,
  previewReceipt: (orderId: string) => `${order(orderId)}/receipts/preview`,
  verifyReceipt: (orderId: string) => `${order(orderId)}/receipts`,
  reverseVerification: (orderId: string) => `${order(orderId)}/receipts/reverse`,
  comments: (orderId: string) => `${order(orderId)}/comments`,
  markRead: (orderId: string) => `${order(orderId)}/read`,
  emailPreview: (orderId: string) => `${order(orderId)}/email/preview`,
  email: (orderId: string) => `${order(orderId)}/email`,
  customers: CUSTOMERS,
  customerList: (params: CustomerListParams, cursor?: string | null, limit: number = LIST_PAGE_SIZE) => {
    const search = new URLSearchParams();
    const q = (params.q ?? "").trim();
    if (q) search.set("q", q);
    if (params.includeInactive) search.set("includeInactive", "true");
    if (cursor) search.set("cursor", cursor);
    search.set("limit", String(limit));
    return `${CUSTOMERS}?${search.toString()}`;
  },
  customer,
  deactivateCustomer: (customerId: string) => `${customer(customerId)}/deactivate`,
  reactivateCustomer: (customerId: string) => `${customer(customerId)}/reactivate`,
} as const;

// ---------------------------------------------------------------------------
// Request functions
// ---------------------------------------------------------------------------

function post<T>(path: string, body?: unknown, method = "POST"): Promise<T> {
  return request<T>(path, { method, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
}

export function fetchWholesaleList(
  params: WholesaleListParams,
  cursor?: string | null,
): Promise<CursorPage<WholesaleOrderSummary>> {
  return request<CursorPage<WholesaleOrderSummary>>(wholesalePaths.list(params, cursor));
}

export function fetchWholesaleSearch(
  query: string,
  cursor?: string | null,
): Promise<CursorPage<WholesaleSearchHit>> {
  return request<CursorPage<WholesaleSearchHit>>(wholesalePaths.search(query, cursor));
}

export function fetchWholesaleDetail(orderId: string): Promise<WholesaleOrderDetail> {
  return request<WholesaleOrderDetail>(wholesalePaths.detail(orderId));
}

export async function fetchWholesaleActivityPage(
  orderId: string,
  cursor: string,
): Promise<CursorPage<ActivityEventView>> {
  const detail = await request<WholesaleOrderDetail>(wholesalePaths.activityPage(orderId, cursor));
  return detail.activity;
}

export function createWholesaleOrder(input: CreateWholesaleOrderInput): Promise<WholesaleMutationResultDto> {
  return post(wholesalePaths.orders, input);
}

/** `PATCH /api/wholesale/orders/:orderId` body: the header patch, the version, and the audited override after a duplicate warning. */
export type UpdateWholesaleHeaderInput = Readonly<{
  patch: WholesaleOrderHeaderPatch;
  meta: MutationMeta;
  duplicateOverrideReason?: string;
}>;

export function updateWholesaleHeader(
  orderId: string,
  input: UpdateWholesaleHeaderInput,
): Promise<WholesaleMutationResultDto> {
  return post(wholesalePaths.detail(orderId), input, "PATCH");
}

export function cancelWholesaleOrder(
  orderId: string,
  input: CancelWholesaleOrderInput,
): Promise<WholesaleMutationResultDto> {
  return post(wholesalePaths.cancel(orderId), input);
}

export function addWholesaleLine(orderId: string, input: AddLineInput): Promise<WholesaleMutationResultDto> {
  return post(wholesalePaths.addLine(orderId), input);
}

export function updateWholesaleLine(
  orderId: string,
  lineId: string,
  input: UpdateLineInput,
): Promise<WholesaleMutationResultDto> {
  return post(wholesalePaths.line(orderId, lineId), input, "PATCH");
}

export function setWholesaleLineException(
  orderId: string,
  lineId: string,
  input: SetLineExceptionInput,
): Promise<WholesaleMutationResultDto> {
  return post(wholesalePaths.lineException(orderId, lineId), input);
}

export function previewWholesaleReceipt(orderId: string, input: PreviewReceiptInput): Promise<PreviewReceiptResult> {
  return post(wholesalePaths.previewReceipt(orderId), input);
}

export function verifyWholesaleReceipt(orderId: string, input: VerifyReceiptInput): Promise<WholesaleMutationResultDto> {
  return post(wholesalePaths.verifyReceipt(orderId), input);
}

export function reverseWholesaleVerification(
  orderId: string,
  input: ReverseVerificationInput,
): Promise<WholesaleMutationResultDto> {
  return post(wholesalePaths.reverseVerification(orderId), input);
}

export function addWholesaleComment(orderId: string, input: AddCommentInput): Promise<CommentView> {
  return post(wholesalePaths.comments(orderId), input);
}

export async function markWholesaleViewed(orderId: string, input: MarkReadInput): Promise<void> {
  await post<unknown>(wholesalePaths.markRead(orderId), input);
}

export function fetchWholesaleEmailPreview(orderId: string): Promise<WholesaleEmailPreview> {
  return request<WholesaleEmailPreview>(wholesalePaths.emailPreview(orderId));
}

/** `POST …/email` — an empty body; the server renders and enqueues one `wholesale_ready` email. */
export function sendWholesaleEmail(orderId: string): Promise<WholesaleEmailView> {
  return post(wholesalePaths.email(orderId));
}

export function fetchCustomerList(
  params: CustomerListParams,
  cursor?: string | null,
): Promise<CursorPage<WholesaleCustomerView>> {
  return request<CursorPage<WholesaleCustomerView>>(wholesalePaths.customerList(params, cursor));
}

export function fetchCustomer(customerId: string): Promise<WholesaleCustomerView> {
  return request<WholesaleCustomerView>(wholesalePaths.customer(customerId));
}

export function createCustomer(input: CreateWholesaleCustomerInput): Promise<WholesaleCustomerView> {
  return post(wholesalePaths.customers, input);
}

export function updateCustomer(customerId: string, input: UpdateWholesaleCustomerInput): Promise<WholesaleCustomerView> {
  return post(wholesalePaths.customer(customerId), input, "PATCH");
}

export function setCustomerActive(
  customerId: string,
  active: boolean,
  expectedVersion: number,
): Promise<WholesaleCustomerView> {
  return post(
    active ? wholesalePaths.reactivateCustomer(customerId) : wholesalePaths.deactivateCustomer(customerId),
    { expectedVersion },
  );
}

// ---------------------------------------------------------------------------
// Errors the wholesale forms name
// ---------------------------------------------------------------------------

function isWholesaleDuplicateCandidate(value: unknown): value is WholesaleDuplicateCandidate {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate["orderId"] === "string" && typeof candidate["number"] === "string";
}

/**
 * The matching orders a 409 `DUPLICATE_WHOLESALE_ORDER` names, or `null`
 * for any other error. A malformed payload yields an empty list: the
 * warning still shows, without links.
 */
export function wholesaleDuplicateCandidatesOf(error: unknown): readonly WholesaleDuplicateCandidate[] | null {
  if (!isClientApiError(error) || error.status !== 409 || error.code !== "DUPLICATE_WHOLESALE_ORDER") return null;
  const details = error.details as { candidates?: unknown } | null;
  const candidates = details?.candidates;
  if (!Array.isArray(candidates)) return [];
  return candidates.filter(isWholesaleDuplicateCandidate);
}

/** The 409s a wholesale command may answer with a message worth showing as it is. */
export const WHOLESALE_CONFLICT_CODES: ReadonlySet<string> = new Set([
  "ORDER_CANCELLED",
  "CUSTOMER_INACTIVE",
  "CUSTOMER_HAS_NO_EMAIL",
  "ORDER_NOT_ARRIVED",
  "CUSTOMER_NAME_IN_USE",
  "DUPLICATE_WHOLESALE_ORDER",
]);

// ---------------------------------------------------------------------------
// Hooks — reads
// ---------------------------------------------------------------------------

export type WholesaleListPages = InfinitePages<WholesaleOrderSummary>;

export function useWholesaleList(
  params: WholesaleListParams,
  options: { enabled?: boolean } = {},
): UseInfiniteQueryResult<WholesaleListPages> {
  return useInfiniteQuery({
    queryKey: wholesaleQueryKeys.list(params),
    queryFn: ({ pageParam }) => fetchWholesaleList(params, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

/** The wholesale search; disabled for a blank query (the server requires 1–120 characters). */
export function useWholesaleSearch(query: string): UseInfiniteQueryResult<InfinitePages<WholesaleSearchHit>> {
  const trimmed = query.trim();
  return useInfiniteQuery({
    queryKey: wholesaleQueryKeys.search(trimmed),
    queryFn: ({ pageParam }) => fetchWholesaleSearch(trimmed, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled: trimmed.length > 0,
  });
}

export function useWholesaleDetail(orderId: string | null): UseQueryResult<WholesaleOrderDetail> {
  return useQuery({
    queryKey: wholesaleQueryKeys.detail(orderId ?? ""),
    queryFn: () => fetchWholesaleDetail(orderId ?? ""),
    enabled: orderId !== null && orderId.length > 0,
  });
}

export type WholesaleCustomerPages = InfinitePages<WholesaleCustomerView>;

export function useCustomerList(
  params: CustomerListParams,
  options: { enabled?: boolean } = {},
): UseInfiniteQueryResult<WholesaleCustomerPages> {
  return useInfiniteQuery({
    queryKey: wholesaleQueryKeys.customerList(params),
    queryFn: ({ pageParam }) => fetchCustomerList(params, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

export function useCustomer(customerId: string | null): UseQueryResult<WholesaleCustomerView> {
  return useQuery({
    queryKey: wholesaleQueryKeys.customer(customerId ?? ""),
    queryFn: () => fetchCustomer(customerId ?? ""),
    enabled: customerId !== null && customerId.length > 0,
  });
}

/** The customer email as Send would enqueue it; read afresh every time the dialog opens. */
export function useWholesaleEmailPreview(orderId: string, enabled: boolean): UseQueryResult<WholesaleEmailPreview> {
  return useQuery({
    queryKey: wholesaleQueryKeys.emailPreview(orderId),
    queryFn: () => fetchWholesaleEmailPreview(orderId),
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

// ---------------------------------------------------------------------------
// Hooks — writes
// ---------------------------------------------------------------------------

function invalidating<TData, TVariables>(
  queryClient: QueryClient,
  mutationFn: (variables: TVariables) => Promise<TData>,
): UseMutationResult<TData, unknown, TVariables> {
  return useMutation({ mutationFn, onSuccess: () => invalidateCaseData(queryClient) });
}

export function useCreateWholesaleOrder(): UseMutationResult<WholesaleMutationResultDto, unknown, CreateWholesaleOrderInput> {
  return invalidating(useQueryClient(), createWholesaleOrder);
}

export function useCancelWholesaleOrder(
  orderId: string,
): UseMutationResult<WholesaleMutationResultDto, unknown, CancelWholesaleOrderInput> {
  return invalidating(useQueryClient(), (input: CancelWholesaleOrderInput) => cancelWholesaleOrder(orderId, input));
}

export function useSendWholesaleEmail(orderId: string): UseMutationResult<WholesaleEmailView, unknown, void> {
  return invalidating(useQueryClient(), () => sendWholesaleEmail(orderId));
}

export function useCreateCustomer(): UseMutationResult<WholesaleCustomerView, unknown, CreateWholesaleCustomerInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCustomer,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: wholesaleQueryKeys.customers() }),
  });
}

export type UpdateCustomerVariables = Readonly<{ customerId: string; input: UpdateWholesaleCustomerInput }>;

export function useUpdateCustomer(): UseMutationResult<WholesaleCustomerView, unknown, UpdateCustomerVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, input }: UpdateCustomerVariables) => updateCustomer(customerId, input),
    // A rename shows on every order that names the customer, so the whole cache goes stale.
    onSuccess: () => invalidateCaseData(queryClient),
  });
}

export type SetCustomerActiveVariables = Readonly<{ customerId: string; active: boolean; expectedVersion: number }>;

export function useSetCustomerActive(): UseMutationResult<WholesaleCustomerView, unknown, SetCustomerActiveVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, active, expectedVersion }: SetCustomerActiveVariables) =>
      setCustomerActive(customerId, active, expectedVersion),
    onSuccess: () => invalidateCaseData(queryClient),
  });
}

export type WholesaleMarkViewedVariables = Readonly<{ orderId: string; throughActivityEventId: string }>;

/** Acknowledges an order as viewed through the server's own read cursor; lists go stale so the unread mark clears. */
export function useMarkWholesaleViewed(): UseMutationResult<void, unknown, WholesaleMarkViewedVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, throughActivityEventId }: WholesaleMarkViewedVariables) =>
      markWholesaleViewed(orderId, { throughActivityEventId }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "wholesale" && (query.queryKey[1] === "list" || query.queryKey[1] === "search"),
      }),
  });
}

/**
 * Appends the server's comment to the cached wholesale detail, moves the
 * author's read cursor through the comment's own event and clears the
 * order's unread flag — the wholesale twin of `applyCommentToCache`.
 */
export function applyWholesaleCommentToCache(queryClient: QueryClient, orderId: string, comment: CommentView): void {
  queryClient.setQueryData<WholesaleOrderDetail>(wholesaleQueryKeys.detail(orderId), (current) => {
    if (!current) return current;
    const comments = current.comments.some((existing) => existing.id === comment.id)
      ? current.comments
      : [...current.comments, comment];
    return {
      ...current,
      comments,
      readThroughActivityEventId: comment.activityEventId,
      order: { ...current.order, unread: false },
    };
  });
}

// ---------------------------------------------------------------------------
// Endpoints for the shared dialogs
// ---------------------------------------------------------------------------

/**
 * What the arrival, reversal, cancellation, exception and comment dialogs
 * need to work a wholesale order instead of a case: the request functions
 * for its routes. The dialogs default to the case ones.
 */
export function wholesaleReceiptEndpoints(orderId: string) {
  return {
    preview: (input: PreviewReceiptInput) => previewWholesaleReceipt(orderId, input),
    verify: (input: VerifyReceiptInput) => verifyWholesaleReceipt(orderId, input),
    reverse: (input: ReverseVerificationInput) => reverseWholesaleVerification(orderId, input),
  } as const;
}

export function wholesaleCommentEndpoints(orderId: string) {
  return {
    add: (input: AddCommentInput) => addWholesaleComment(orderId, input),
    afterAdd: async (queryClient: QueryClient, comment: CommentView) => {
      applyWholesaleCommentToCache(queryClient, orderId, comment);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: wholesaleQueryKeys.detail(orderId), refetchType: "none" }),
        queryClient.invalidateQueries({ queryKey: wholesaleQueryKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: wholesaleQueryKeys.searches() }),
      ]);
    },
  } as const;
}
